import { useEffect } from 'react';
import { useTenantContext } from '../context/TenantContext';

/**
 * Hook pour mettre à jour dynamiquement les éléments HTML <head> 
 * en fonction du tenant actuel (multi-tenant par domaine)
 */
export function useDynamicHTML() {
  const { tenant } = useTenantContext();

  useEffect(() => {
    if (!tenant) return;

    // 1. Mettre à jour le titre de la page
    const title = tenant.config?.seo?.title || tenant.name || 'SIS';
    document.title = title;

    // 2. Mettre à jour la description meta
    const description = tenant.config?.seo?.description || `Bienvenue sur ${tenant.name}`;
    updateMetaTag('name', 'description', description);

    // 3. Mettre à jour les keywords meta
    const keywords = tenant.config?.seo?.keywords || '';
    if (keywords) {
      updateMetaTag('name', 'keywords', keywords);
    }

    // 4. Mettre à jour l'auteur
    updateMetaTag('name', 'author', tenant.name);

    // 5. Mettre à jour le canonical URL
    const domain = tenant.domain_name || window.location.hostname;
    const canonicalUrl = `https://${domain}`;
    updateLinkTag('canonical', canonicalUrl);

    // 6. Mettre à jour le favicon (utilise config.icon si défini, sinon le logo)
    const faviconUrl = tenant.config?.icon || tenant.logo;
    if (faviconUrl) {
      updateFavicon(faviconUrl);
    }

    // 7. Open Graph tags pour les réseaux sociaux
    updateMetaTag('property', 'og:title', title);
    updateMetaTag('property', 'og:description', description);
    updateMetaTag('property', 'og:url', canonicalUrl);
    if (tenant.logo) {
      updateMetaTag('property', 'og:image', tenant.logo);
    }
    updateMetaTag('property', 'og:type', 'website');
    updateMetaTag('property', 'og:site_name', tenant.name);

    // 8. Twitter Card tags
    updateMetaTag('name', 'twitter:card', 'summary_large_image');
    updateMetaTag('name', 'twitter:title', title);
    updateMetaTag('name', 'twitter:description', description);
    if (tenant.logo) {
      updateMetaTag('name', 'twitter:image', tenant.logo);
    }

    // 9. Theme color (couleur de la barre d'adresse mobile)
    const themeColor = tenant.config?.color1 || tenant.config?.color2 || '#008aef';
    updateMetaTag('name', 'theme-color', themeColor);

  }, [tenant]);
}

/**
 * Fonction utilitaire pour mettre à jour ou créer une balise <meta>
 */
function updateMetaTag(attribute: 'name' | 'property', value: string, content: string) {
  let element = document.querySelector(`meta[${attribute}="${value}"]`) as HTMLMetaElement;
  
  if (!element) {
    element = document.createElement('meta');
    element.setAttribute(attribute, value);
    document.head.appendChild(element);
  }
  
  element.setAttribute('content', content);
}

/**
 * Fonction utilitaire pour mettre à jour ou créer une balise <link>
 */
function updateLinkTag(rel: string, href: string) {
  let element = document.querySelector(`link[rel="${rel}"]`) as HTMLLinkElement;
  
  if (!element) {
    element = document.createElement('link');
    element.setAttribute('rel', rel);
    document.head.appendChild(element);
  }
  
  element.setAttribute('href', href);
}

/**
 * Fonction utilitaire pour mettre à jour le favicon
 */
function updateFavicon(iconUrl: string) {
  // Supprimer les anciens favicons
  const existingIcons = document.querySelectorAll('link[rel*="icon"]');
  existingIcons.forEach(icon => icon.remove());

  // Ajouter le nouveau favicon
  const link = document.createElement('link');
  link.rel = 'icon';
  link.type = 'image/png';
  link.href = iconUrl;
  document.head.appendChild(link);

  // Ajouter aussi apple-touch-icon pour iOS
  const appleIcon = document.createElement('link');
  appleIcon.rel = 'apple-touch-icon';
  appleIcon.href = iconUrl;
  document.head.appendChild(appleIcon);
}
